// ผูกเพจ Inbox Center กับแบรนด์/กลุ่ม — ติ๊กได้หลายเพจ ทั้ง Facebook และ LINE OA
//
// ดึงรายการเพจไม่ได้ (ยังไม่ login ที่ Inbox Center) → สลับไปโหมดกรอก Page ID เอง
// ไม่ปล่อยให้ตั้งค่าไม่ได้เลย เพราะ id ดูได้จากหน้า Connect ของเขา
import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { IconChevronDown, IconSearch } from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";
import { updateBrand, type PickablePage } from "../api/settings-api";
import { useInvalidateSettings, usePickablePages } from "../api/use-settings";
import { pickColor } from "../model/setting-menus";

function pickedStatus(ids: string[], byId: (id: string) => PickablePage | undefined) {
  if (!ids.length) return "ยังไม่ล็อกเพจ — แสดงทุกเพจ";
  const names = ids.map((id) => byId(id)?.name || id);
  return ids.length === 1
    ? `ล็อกอยู่ที่เพจ ${names[0]}`
    : `ล็อกอยู่ ${ids.length} เพจ: ${names.join(", ")}`;
}

export function PagePicker({ brand }: { brand: Brand }) {
  const current = brand.pageIds ?? [];
  const { data: pages, isPending, isError } = usePickablePages();
  // ยังไม่ login = query ถูกปิดไว้ จึงค้างสถานะ pending ตลอด ต้องเช็คเองไม่ให้ค้างข้อความ
  // "กำลังโหลด" (ของเดิมตกไปโหมดกรอก Page ID เองทันที)
  const hasToken = !!wazzupToken();
  const invalidate = useInvalidateSettings();
  const [status, setStatus] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (pageIds: string[]) => updateBrand(brand.id, { pageIds }),
    onMutate: () => setStatus("กำลังบันทึก…"),
    onSuccess: (saved) => {
      setStatus(
        "บันทึกแล้ว — " +
          pickedStatus(saved.pageIds ?? [], (id) => (pages ?? []).find((p) => p.id === id)),
      );
      invalidate();
    },
    onError: () => setStatus("บันทึกไม่สำเร็จ กรุณาลองใหม่"),
  });

  if (hasToken && isPending) return <p className="muted">กำลังโหลดรายการเพจ…</p>;

  // ไม่มีรายการเพจ (ยังไม่ login / ไม่มีเพจ) → ให้กรอก Page ID เอง (คั่นคอมมาได้)
  if (!hasToken || isError || !pages?.length) {
    return (
      <ManualPageInput
        current={current}
        reason={
          !hasToken || isError
            ? "ดึงรายการเพจอัตโนมัติไม่ได้ (ต้อง login Inbox Center ที่หน้า Message ก่อน) — ระหว่างนี้กรอก Page ID เองได้ ดูได้จากหน้า Connect ของ Inbox Center"
            : "บัญชีนี้ยังไม่มีเพจที่เชื่อมไว้"
        }
        status={status ?? pickedStatus(current, () => undefined)}
        saving={save.isPending}
        onSave={(ids) => save.mutate(ids)}
      />
    );
  }

  return (
    <PageDropdown
      pages={pages}
      current={current}
      status={status ?? pickedStatus(current, (id) => pages.find((p) => p.id === id))}
      saving={save.isPending}
      onSave={(ids) => save.mutate(ids)}
    />
  );
}

function ManualPageInput({
  current,
  reason,
  status,
  saving,
  onSave,
}: {
  current: string[];
  reason: string;
  status: string;
  saving: boolean;
  onSave: (ids: string[]) => void;
}) {
  const [text, setText] = useState(current.join(", "));
  return (
    <>
      <div className="page-row">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="กรอก Page ID คั่นด้วยคอมมาได้ (เว้นว่าง = ไม่ล็อกเพจ)"
        />
        <button
          type="button"
          className="btn-primary btn-sm"
          disabled={saving}
          onClick={() =>
            onSave(
              text
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
        >
          บันทึก
        </button>
      </div>
      <p className="muted small-note">{reason}</p>
      <p className="save-status muted">{status}</p>
    </>
  );
}

function PageAvatar({ page }: { page: PickablePage | null }) {
  const [failed, setFailed] = useState(false);
  if (!page) return <span className="pg-ava-fb pg-ava-all">∀</span>;
  if (page.pictureUrl && !failed)
    return <img className="pg-ava" src={page.pictureUrl} alt="" onError={() => setFailed(true)} />;
  return (
    <span className="pg-ava-fb" style={{ background: pickColor(page.id) }}>
      {(page.name || "?").charAt(0)}
    </span>
  );
}

function PageDropdown({
  pages,
  current,
  status,
  saving,
  onSave,
}: {
  pages: PickablePage[];
  current: string[];
  status: string;
  saving: boolean;
  onSave: (ids: string[]) => void;
}) {
  const [picked, setPicked] = useState<string[]>(current);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef<HTMLDivElement>(null);

  // ปิดเมนูเมื่อคลิกที่อื่นหรือกด Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const byId = (id: string) => pages.find((p) => p.id === id);
  const chosen = picked.map(byId).filter((p) => !!p);
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? pages.filter((p) => p.name.toLowerCase().includes(needle) || p.id.includes(needle))
    : pages;

  const toggle = (id: string) => {
    // เลือก "ทั้งหมด" = ล้างทั้งหมด · เมนูไม่ปิดหลังคลิก ให้เลือกต่อได้
    if (!id) setPicked([]);
    else setPicked((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));
  };

  return (
    <>
      <div className="page-row">
        <div className="pgpick" ref={wrap}>
          <button
            type="button"
            className="pgpick-btn"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            {!chosen.length ? (
              <>
                <PageAvatar page={null} />
                <span className="pg-name">— ทั้งหมด (ไม่ล็อกเพจ) —</span>
              </>
            ) : chosen.length === 1 ? (
              <>
                <PageAvatar page={chosen[0]} />
                <span className="pg-name">{chosen[0].name || chosen[0].id}</span>
                <span className="code-chip">{chosen[0].id}</span>
              </>
            ) : (
              <>
                {/* หลายเพจ: โชว์รูปซ้อนกัน + จำนวน */}
                <span className="pg-ava-stack">
                  {chosen.slice(0, 3).map((p) => (
                    <PageAvatar key={p.id} page={p} />
                  ))}
                </span>
                <span className="pg-name">
                  {chosen.length} เพจ · {chosen.map((p) => p.name || p.id).join(", ")}
                </span>
              </>
            )}
            <IconChevronDown />
          </button>
          <div className={cn("pgpick-menu", open && "open")} onClick={(e) => e.stopPropagation()}>
            <div className="pgpick-search search-box">
              <IconSearch />
              <input
                type="text"
                placeholder="ค้นหาเพจ..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div className="pgpick-list">
              {!needle && (
                <button
                  type="button"
                  className={cn("pgpick-item", !picked.length && "active")}
                  onClick={() => toggle("")}
                >
                  <span className="pg-check">{picked.length ? "" : "✓"}</span>
                  <PageAvatar page={null} />
                  <span className="pg-name">— ทั้งหมด (ไม่ล็อกเพจ) —</span>
                </button>
              )}
              {shown.length ? (
                shown.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={cn("pgpick-item", picked.includes(p.id) && "active")}
                    onClick={() => toggle(p.id)}
                  >
                    <span className="pg-check">{picked.includes(p.id) ? "✓" : ""}</span>
                    <PageAvatar page={p} />
                    <span className="pg-name">{p.name || p.id}</span>
                    <span className="code-chip">{p.id}</span>
                  </button>
                ))
              ) : (
                <p className="muted" style={{ padding: 10 }}>
                  ไม่พบเพจที่ค้นหา
                </p>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="btn-primary btn-sm"
          disabled={saving}
          onClick={() => onSave(picked)}
        >
          บันทึก
        </button>
      </div>
      <p className="save-status muted">{status}</p>
    </>
  );
}
